import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlay, FaUsers, FaGlobe, FaAward, FaLightbulb, FaHandshake, FaRocket, FaCog, FaChartLine, FaBuilding, FaClock, FaCubes, FaWrench, FaStar, FaSpaceShuttle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ReactPlayer from 'react-player';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import SEO from '../../components/SEO';
import Spinner from '../../components/Spinner';
import './About.css';

// Icon mapping for dynamic icons from Firebase
const ICON_MAP = {
  rocket: <FaRocket />,
  users: <FaUsers />,
  lightbulb: <FaLightbulb />,
  award: <FaAward />,
  globe: <FaGlobe />,
  cog: <FaCog />,
  chart: <FaChartLine />,
  building: <FaBuilding />,
  handshake: <FaHandshake />,
  clock: <FaClock />,
  cubes: <FaCubes />,
  wrench: <FaWrench />,
  star: <FaStar />,
  shuttle: <FaSpaceShuttle />
};

const getIcon = (iconName) => ICON_MAP[iconName] || <FaRocket />;

// Default data arrays defined outside component to avoid recreation
const DEFAULT_TIMELINE = [
  {
    year: '2018',
    title: 'Fondation d\'OPTEAM Services',
    description: 'Création de l\'entreprise par Jean-Christophe Lhotellier et Rémi Coquet',
    icon: <FaRocket />
  },
  {
    year: '2019',
    title: 'Premiers projets RPA',
    description: 'Déploiement réussi de solutions UiPath chez nos premiers clients',
    icon: <FaUsers />
  },
  {
    year: '2020',
    title: 'Expansion technologique',
    description: 'Ajout de Blue Prism et Automation Anywhere à notre portfolio',
    icon: <FaLightbulb />
  },
  {
    year: '2021',
    title: 'Reconnaissance marché',
    description: 'Partenariat avec LVMH et Groupe Rossel',
    icon: <FaAward />
  },
  {
    year: '2022',
    title: 'Innovation IA',
    description: 'Intégration de l\'IA dans nos solutions d\'automatisation',
    icon: <FaRocket />
  },
  {
    year: '2024',
    title: 'Expansion européenne',
    description: 'Ouverture vers les marchés européens',
    icon: <FaGlobe />
  }
];

const DEFAULT_ACHIEVEMENTS = [
  {
    number: '50+',
    label: 'Projets réalisés',
    icon: <FaRocket />
  },
  {
    number: '25M€',
    label: 'Économies générées',
    icon: <FaAward />
  },
  {
    number: '10,000+',
    label: 'Utilisateurs impactés',
    icon: <FaUsers />
  },
  {
    number: '15',
    label: 'Pays desservis',
    icon: <FaGlobe />
  }
];

const About = () => {
  const { t, i18n } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [videoRef, videoInView] = useInView({
    triggerOnce: true,
    threshold: 0.3
  });

  // State for Firebase data - initialize with defaults
  const [timeline, setTimeline] = useState(DEFAULT_TIMELINE);
  const [achievements, setAchievements] = useState(DEFAULT_ACHIEVEMENTS);
  const [timelineRawData, setTimelineRawData] = useState([]);
  const [achievementsRawData, setAchievementsRawData] = useState([]);
  const [founders, setFounders] = useState([]);
  const [foundersLoading, setFoundersLoading] = useState(true);

  // Get current language
  const currentLang = i18n.language?.startsWith('fr') ? 'fr' : 'en';

  const values = [
    {
      icon: <img src="/assets/images/Ai-Technology-Spark Streamline Core-Neon.svg" alt="" className="value-icon-img" />,
      title: t('about.values.innovation.title'),
      description: t('about.values.innovation.description')
    },
    {
      icon: <img src="/assets/images/Business-Handshake Streamline Core-Neon.svg" alt="" className="value-icon-img" />,
      title: t('about.values.partnership.title'),
      description: t('about.values.partnership.description')
    },
    {
      icon: <img src="/assets/images/Star-Badge Streamline Core-Neon.svg" alt="" className="value-icon-img" />,
      title: t('about.values.excellence.title'),
      description: t('about.values.excellence.description')
    },
    {
      icon: <img src="/assets/images/Startup-Light Streamline Core-Neon.svg" alt="" className="value-icon-img" />,
      title: t('about.values.agility.title'),
      description: t('about.values.agility.description')
    }
  ];

  // Fetch timeline from Firebase
  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        const q = query(collection(db, 'timeline'), orderBy('order', 'asc'));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const items = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setTimelineRawData(items);
        }
      } catch (err) {
        console.log('Using default timeline (Firestore not configured or empty)');
      }
    };
    fetchTimeline();
  }, []);

  // Fetch achievements from Firebase
  useEffect(() => {
    const fetchAchievements = async () => {
      try {
        const q = query(collection(db, 'achievements'), orderBy('order', 'asc'));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const items = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setAchievementsRawData(items);
        }
      } catch (err) {
        console.log('Using default achievements (Firestore not configured or empty)');
      }
    };
    fetchAchievements();
  }, []);

  // Fetch founders from Firebase
  useEffect(() => {
    const fetchFounders = async () => {
      setFoundersLoading(true);
      try {
        const q = query(collection(db, 'founders'), orderBy('order', 'asc'));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const items = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setFounders(items);
        }
      } catch (err) {
        console.log('Using default founders (Firestore not configured or empty)');
      } finally {
        setFoundersLoading(false);
      }
    };
    fetchFounders();
  }, []);

  // Update timeline when language changes or raw data updates
  useEffect(() => {
    if (timelineRawData.length > 0) {
      const localizedItems = timelineRawData.map(item => ({
        ...item,
        title: currentLang === 'fr'
          ? (item.title_fr || item.title_en || item.title)
          : (item.title_en || item.title),
        description: currentLang === 'fr'
          ? (item.description_fr || item.description_en || item.description)
          : (item.description_en || item.description),
        icon: getIcon(item.icon)
      }));
      setTimeline(localizedItems);
    } else {
      setTimeline(DEFAULT_TIMELINE);
    }
  }, [timelineRawData, currentLang]);

  // Update achievements when language changes or raw data updates
  useEffect(() => {
    if (achievementsRawData.length > 0) {
      const localizedItems = achievementsRawData.map(item => ({
        ...item,
        label: currentLang === 'fr'
          ? (item.label_fr || item.label_en || item.label)
          : (item.label_en || item.label)
      }));
      setAchievements(localizedItems);
    } else {
      setAchievements(DEFAULT_ACHIEVEMENTS);
    }
  }, [achievementsRawData, currentLang]);

  const isFr = currentLang === 'fr';

  return (
    <div className="about">
      <SEO
        title={isFr ? 'À Propos - Notre Histoire & Mission' : 'About Us - Our Story & Mission'}
        description={isFr
          ? 'Découvrez OPTEAM Services : notre histoire, notre mission et notre vision. Experts en automatisation RPA, IA et Low-Code depuis plusieurs années.'
          : 'Discover OPTEAM Services: our story, mission and vision. Experts in RPA automation, AI and Low-Code for years.'
        }
        canonical="/about"
      />
      {/* Hero Section */}
      <section className="about-hero">
        <div className="hero-background">
          <img
            src="/assets/images/business-handshake.jpg"
            alt="About"
          />
          <div className="overlay"></div>
        </div>
        <div className="container">
          <motion.div
            className="about-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>{t('about.title')}</h1>
            <p>{t('about.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="company-story" ref={ref}>
        <div className="container">
          <div className="story-content">
            <motion.div
              className="story-text"
              initial={{ opacity: 0, x: -30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <h2>{t('about.story.title')}</h2>
              <p>{t('about.story.content')}</p>

              <div className="story-points">
                <div className="story-point">
                  <h3>{t('about.mission.title')}</h3>
                  <p>{t('about.mission.content')}</p>
                </div>
                <div className="story-point">
                  <h3>{t('about.vision.title')}</h3>
                  <p>{t('about.vision.content')}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="about-story-image"
              initial={{ opacity: 0, x: 30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src="/assets/images/520d4960ea9d2c1dd77b54a7d5768fc0835da584.png"
                alt="Team collaboration"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CEO Video Section */}
      <section className="ceo-video" ref={videoRef}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={videoInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2>{t('about.ceo.title')}</h2>
            <p>{t('about.ceo.content')}</p>

            {foundersLoading ? (
              <Spinner size="medium" text={currentLang === 'fr' ? 'Chargement...' : 'Loading...'} />
            ) : (
              <div className="video-container">
                <div className="video-wrapper">
                  <ReactPlayer
                    url="/assets/videos/opteam_services_interview.mp4"
                    width="100%"
                    height="100%"
                    controls={true}
                    light={founders.length > 0 && (founders[0].videoThumbUrl || founders[0].imageUrl) ? (founders[0].videoThumbUrl || founders[0].imageUrl) : "/assets/images/person-professional-1.jpg"}
                    playIcon={
                      <div className="play-icon">
                        <FaPlay />
                      </div>
                    }
                  />
                </div>

                <div className="video-info">
                  <div className="ceo-profile">
                    <img
                      src={founders.length > 0 && founders[0].imageUrl ? founders[0].imageUrl : "/assets/images/person-professional-1.jpg"}
                      alt={founders.length > 0 ? founders[0].name : "Jean-Christophe Lhotellier"}
                    />
                    <div className="ceo-details">
                      <h3>{founders.length > 0 ? founders[0].name : "Jean-Christophe Lhotellier"}</h3>
                      <p>{founders.length > 0 ? (currentLang === 'fr' ? (founders[0].title_fr || founders[0].title_en) : (founders[0].title_en || founders[0].title_fr)) : (currentLang === 'fr' ? "Co-Fondateur & CEO" : "Co-Founder & CEO")}</p>
                      <p>{founders.length > 0 ? (currentLang === 'fr' ? (founders[0].bio_fr || founders[0].bio_en) : (founders[0].bio_en || founders[0].bio_fr)) : (currentLang === 'fr' ? "Expert en automatisation avec plus de 15 ans d'expérience" : "Automation expert with over 15 years of experience")}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Company Values */}
      <section className="company-values">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2>{t('about.values.title')}</h2>
            <div className="values-grid">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  className="value-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className="value-icon">
                    {value.icon}
                  </div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="company-timeline">
        <div className="container">
          <h2>{t('about.timeline.title')}</h2>
          <p className="section-subtitle">{t('about.timeline.subtitle')}</p>
          <div className="timeline">
            {timeline.map((item, index) => (
              <motion.div
                key={item.id || index}
                className="timeline-item"
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <div className="timeline-icon">
                  {item.icon}
                </div>
                <div className="timeline-content">
                  <div className="timeline-text">
                    <div className="timeline-year">{item.year}</div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="achievements">
        <div className="container">
          <h2>{t('about.achievements.title')}</h2>
          <div className="achievements-grid">
            {achievements.map((achievement, index) => (
              <div key={achievement.id || index} className="achievement-item">
                <div className="achievement-icon">
                  {achievement.iconUrl ? (
                    <img src={achievement.iconUrl} alt="" className="achievement-icon-img" />
                  ) : (
                    achievement.icon
                  )}
                </div>
                <div className="achievement-number">{achievement.number}</div>
                <div className="achievement-label">{achievement.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="leadership-team">
        <div className="container">
          <h2>{currentLang === 'fr' ? 'Notre Équipe de Direction' : 'Our Leadership Team'}</h2>
          <div className="leaders-grid">
            {founders.length > 0 ? (
              founders.map((founder, index) => (
                <motion.div
                  key={founder.id}
                  className="leader-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                >
                  <div className="leader-image">
                    <img
                      src={founder.imageUrl || '/assets/images/person-professional-1.jpg'}
                      alt={founder.name}
                    />
                  </div>
                  <div className="leader-info">
                    <h3>{founder.name}</h3>
                    <p className="leader-title">
                      {currentLang === 'fr' ? (founder.title_fr || founder.title_en) : (founder.title_en || founder.title_fr)}
                    </p>
                    <p className="leader-bio">
                      {currentLang === 'fr' ? (founder.bio_fr || founder.bio_en) : (founder.bio_en || founder.bio_fr)}
                    </p>
                  </div>
                </motion.div>
              ))
            ) : (
              <>
                <motion.div
                  className="leader-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6 }}
                >
                  <div className="leader-image">
                    <img
                      src="/assets/images/person-professional-1.jpg"
                      alt="Jean-Christophe Lhotellier"
                    />
                  </div>
                  <div className="leader-info">
                    <h3>Jean-Christophe Lhotellier</h3>
                    <p className="leader-title">{currentLang === 'fr' ? 'Co-Fondateur & CEO' : 'Co-Founder & CEO'}</p>
                    <p className="leader-bio">{currentLang === 'fr' ? "Expert en automatisation avec plus de 15 ans d'expérience dans la transformation digitale des entreprises." : "Automation expert with over 15 years of experience in enterprise digital transformation."}</p>
                  </div>
                </motion.div>

                <motion.div
                  className="leader-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <div className="leader-image">
                    <img
                      src="/assets/images/person-professional-2.jpg"
                      alt="Rémi Coquet"
                    />
                  </div>
                  <div className="leader-info">
                    <h3>Rémi Coquet</h3>
                    <p className="leader-title">{currentLang === 'fr' ? 'Co-Fondateur & CTO' : 'Co-Founder & CTO'}</p>
                    <p className="leader-bio">{currentLang === 'fr' ? "Architecte technique spécialisé en RPA et intelligence artificielle, pionnier des solutions d'automatisation." : "Technical architect specialized in RPA and artificial intelligence, pioneer of automation solutions."}</p>
                  </div>
                </motion.div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
